import { CommonModule } from '@angular/common';
import {
	Component,
	HostBinding,
	OnDestroy,
	OnInit,
	AfterViewInit,
	Renderer2,
	Signal,
	ViewChild,
	computed,
	inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';

import { StreamData } from '../../shared/stream.token';
import { VbLxpVideoPlayerComponent } from '../video-player/vb-lxp-video-player.component';

@Component({
	selector: 'vb-lxp-video-popout-page',
	standalone: true,
	imports: [CommonModule, VbLxpVideoPlayerComponent],
	templateUrl: './vb-lxp-video-popout-page.component.html',
	styleUrl: './vb-lxp-video-popout-page.component.css',
	host: {
		style: 'display: flex; flex-direction: column; height: 100%; background: #000;',
	},
})
export class VbLxpVideoPopoutPageComponent implements OnInit, AfterViewInit, OnDestroy {
	private static readonly AUTOPLAY_MAX_ATTEMPTS = 60;
	private static readonly AUTOPLAY_RETRY_DELAY_MS = 150;
	private static readonly PLAYER_INIT_MAX_ATTEMPTS = 30;
	private static readonly PLAYER_INIT_RETRY_DELAY_MS = 100;

	private readonly route = inject(ActivatedRoute);
	private readonly renderer = inject(Renderer2);
	private readonly queryParamMap = toSignal(this.route.queryParamMap);
	private readonly fullscreenStyles = ['margin', 'padding', 'height', 'overflow'];

	@ViewChild('player', { static: false })
	private playerComponent: VbLxpVideoPlayerComponent | undefined;
	private autoplayTimeoutId: ReturnType<typeof setTimeout> | null = null;
	private playerInitTimeoutId: ReturnType<typeof setTimeout> | null = null;
	private playerInitialized = false;
	private removeFocusListener: (() => void) | null = null;
	private removeVisibilityListener: (() => void) | null = null;
	private removeLoadListener: (() => void) | null = null;

	public readonly playbackUrl: Signal<string> = computed(
		() => this.queryParamMap()?.get('playbackUrl')?.trim() ?? '',
	);

	public readonly streamTitle: Signal<string> = computed(
		() => this.queryParamMap()?.get('title')?.trim() ?? 'Stream',
	);

	public readonly carNumber: Signal<string> = computed(
		() => this.queryParamMap()?.get('carNumber')?.trim() ?? '',
	);

	public readonly driverName: Signal<string> = computed(
		() => this.queryParamMap()?.get('driverName')?.trim() ?? '',
	);

	public readonly isLive: Signal<boolean> = computed(() => {
		const value = this.queryParamMap()?.get('isLive')?.trim().toLowerCase();
		if (value === 'false') {
			return false;
		}

		return true;
	});

	ngOnInit(): void {
		// Apply styles to body and html following the pattern
		this.renderer.setStyle(document.body, 'margin', '0');
		this.renderer.setStyle(document.body, 'padding', '0');
		this.renderer.setStyle(document.body, 'height', '100%');
		this.renderer.setStyle(document.body, 'overflow', 'hidden');
		this.renderer.setStyle(document.documentElement, 'margin', '0');
		this.renderer.setStyle(document.documentElement, 'padding', '0');
		this.renderer.setStyle(document.documentElement, 'height', '100%');
		this.renderer.setStyle(document.documentElement, 'overflow', 'hidden');

		// Retry autoplay on common popup lifecycle events where browsers release media restrictions.
		this.removeFocusListener = this.renderer.listen('window', 'focus', () => {
			this.tryAutoplay();
		});

		this.removeVisibilityListener = this.renderer.listen(document, 'visibilitychange', () => {
			if (document.visibilityState === 'visible') {
				this.tryAutoplay();
			}
		});

		this.removeLoadListener = this.renderer.listen('window', 'load', () => {
			this.tryAutoplay();
		});
	}

	ngAfterViewInit(): void {
		this.initializePlayerForPopout();
	}

	ngOnDestroy(): void {
		this.clearAutoplayTimeout();
		this.clearPlayerInitTimeout();
		this.removeFocusListener?.();
		this.removeFocusListener = null;
		this.removeVisibilityListener?.();
		this.removeVisibilityListener = null;
		this.removeLoadListener?.();
		this.removeLoadListener = null;

		// Remove styles when leaving the page
		this.renderer.removeStyle(document.body, 'margin');
		this.renderer.removeStyle(document.body, 'padding');
		this.renderer.removeStyle(document.body, 'height');
		this.renderer.removeStyle(document.body, 'overflow');
		this.renderer.removeStyle(document.documentElement, 'margin');
		this.renderer.removeStyle(document.documentElement, 'padding');
		this.renderer.removeStyle(document.documentElement, 'height');
		this.renderer.removeStyle(document.documentElement, 'overflow');
	}

	private tryAutoplay(attempt: number = 0): void {
		const player = this.playerComponent;
		const vgApi = player?.vgAPI;

		if (!player || !vgApi) {
			if (attempt >= VbLxpVideoPopoutPageComponent.AUTOPLAY_MAX_ATTEMPTS) {
				return;
			}

			this.clearAutoplayTimeout();
			this.autoplayTimeoutId = setTimeout(() => {
				this.tryAutoplay(attempt + 1);
			}, VbLxpVideoPopoutPageComponent.AUTOPLAY_RETRY_DELAY_MS);
			return;
		}

		this.clearAutoplayTimeout();
		vgApi.setVolume(0);

		Promise.resolve(vgApi.play())
			.catch(() => {
				return vgApi.play();
			})
			.catch(() => {
				if (attempt >= VbLxpVideoPopoutPageComponent.AUTOPLAY_MAX_ATTEMPTS) {
					return;
				}

				this.clearAutoplayTimeout();
				this.autoplayTimeoutId = setTimeout(() => {
					this.tryAutoplay(attempt + 1);
				}, VbLxpVideoPopoutPageComponent.AUTOPLAY_RETRY_DELAY_MS);
			});
	}

	private initializePlayerForPopout(attempt: number = 0): void {
		if (this.playerInitialized) {
			return;
		}

		const playbackUrl = this.playbackUrl();
		const player = this.playerComponent;

		if (!playbackUrl || !player) {
			if (attempt >= VbLxpVideoPopoutPageComponent.PLAYER_INIT_MAX_ATTEMPTS) {
				return;
			}

			this.clearPlayerInitTimeout();
			this.playerInitTimeoutId = setTimeout(() => {
				this.initializePlayerForPopout(attempt + 1);
			}, VbLxpVideoPopoutPageComponent.PLAYER_INIT_RETRY_DELAY_MS);
			return;
		}

		this.clearPlayerInitTimeout();
		this.playerInitialized = true;
		player.setStreamData(this.buildStreamData(playbackUrl, this.streamTitle()));
		this.tryAutoplay();
	}

	private clearAutoplayTimeout(): void {
		if (!this.autoplayTimeoutId) {
			return;
		}

		clearTimeout(this.autoplayTimeoutId);
		this.autoplayTimeoutId = null;
	}

	private clearPlayerInitTimeout(): void {
		if (!this.playerInitTimeoutId) {
			return;
		}

		clearTimeout(this.playerInitTimeoutId);
		this.playerInitTimeoutId = null;
	}

	private buildStreamData(playbackUrl: string, title: string): StreamData {
		const carNum = this.carNumber();
		return {
			id: 'popout-stream',
			teamName: title,
			carNumber: carNum ? carNum : undefined,
			isLive: this.isLive(),
			hlsPlaybackUrl: playbackUrl,
			hlsPromotionalVideoUrl: playbackUrl,
		};
	}
}
